import { ImageWithCrops } from '@/services/platform/api/model';
import { ImageMap } from '@/lib/core/utils/image';

/**
 * A single accepted-card tile.
 *
 * The design draws each brand as a fixed 35x24 bordered tile with the brand logo inset at its
 * own intrinsic size — the logo never fills the tile. Both the outer box and the inner glyph are
 * therefore sized explicitly rather than stretched, so unlike logos keep their real proportions.
 */
interface CardBrandTileProps {
  src: string;
  alt: string;
  /** Intrinsic logo width in px, measured from the design. */
  logoWidth: number;
  /** Intrinsic logo height in px, measured from the design. */
  logoHeight: number;
  /** Tile background — Amex is the only brand with a coloured tile. */
  background?: string;
}

function CardBrandTile({ src, alt, logoWidth, logoHeight, background }: CardBrandTileProps) {
  return (
    <div
      className="flex h-6 w-8.75 shrink-0 items-center justify-center rounded-sm border border-solid border-[#d9d9d9]"
      style={{ backgroundColor: background ?? '#ffffff' }}
    >
      <img
        src={src}
        alt={alt}
        width={logoWidth}
        height={logoHeight}
        style={{ width: logoWidth, height: logoHeight }}
      />
    </div>
  );
}

/** Brand logo geometry taken from Figma node 2364:69688 (the accepted-cards row). */
const FALLBACK_BRANDS: CardBrandTileProps[] = [
  { src: '/images/cards/visa.svg', alt: 'Visa', logoWidth: 24.5, logoHeight: 7.82 },
  { src: '/images/cards/mastercard.svg', alt: 'Mastercard', logoWidth: 22.5, logoHeight: 13.81 },
  { src: '/images/cards/maestro.svg', alt: 'Maestro', logoWidth: 22.5, logoHeight: 13.81 },
  {
    src: '/images/cards/amex.svg',
    alt: 'American Express',
    logoWidth: 28.19,
    logoHeight: 7.26,
    background: '#1f72cd',
  },
];

interface CardBrandsProps {
  label?: string;
  /** CMS-managed brand images. Currently unset in Umbraco, so the local fallback set is used. */
  images?: ImageWithCrops[] | null;
}

/**
 * The "Accepted cards" section shown under the add/edit card forms.
 *
 * Prefers CMS-authored images so editors can change the accepted brands without a release, and
 * falls back to the four brands drawn in the design when the CMS field is empty.
 */
export function CardBrands({ label, images }: CardBrandsProps) {
  const cmsImages = (images ?? []).map(image => ImageMap(image)).filter(image => !!image?.src);

  return (
    <div className="flex flex-col gap-2.5">
      {label && (
        <p className="font-inter text-jncquoi-neutral-1000 text-[16px] font-semibold tracking-[0.25px]">
          {label}
        </p>
      )}
      <div className="flex items-center gap-1">
        {cmsImages.length > 0
          ? cmsImages.map((image, index) => (
              <img
                key={image!.src ?? index}
                src={image!.src}
                alt={image!.alt ?? ''}
                className="h-6 w-auto shrink-0 rounded-sm"
              />
            ))
          : FALLBACK_BRANDS.map(brand => <CardBrandTile key={brand.alt} {...brand} />)}
      </div>
    </div>
  );
}
